// Org roles tab — the organization's CUSTOM, org-owned RBAC roles.
//
// REDESIGN (R2): the old "wall of permission pills" list is replaced by a clean
// SUMMARY list + a FOCUSED single-role editor:
//   1. Roles LIST — one summary card per role: NAME + a one-line plain-English
//      group summary (the permission AREAS it touches, e.g. "Operations +
//      Programs", NOT the 15 individual labels) + an honest member count.
//   2. Clicking a card (or "New role") opens the FOCUSED editor, where
//      permissions are grouped toggles with server-authoritative locking. The
//      editor lives in role-editor-dialog.tsx.
//
// Backend endpoints (app/routers/permission.py, prefix /permissions):
//   GET    /orgs/{orgId}/roles          → OrgRoleResponse[] (carries member_count)
//   POST   /orgs/{orgId}/roles          → create
//   PATCH  /roles/{roleId}              → edit
//   DELETE /roles/{roleId}              → delete
//   GET    /orgs/{orgId}/grantable-permissions → editor locking (server-driven)
//
// HONESTY: never render a raw role_id / UUID; show an honest empty / loading /
// error state; member_count comes straight from the backend (no fabrication).

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState } from "react";
import {
  deleteRole,
  listOrgRoles,
  roleGroupSummary,
  type OrgRole,
} from "@/libs/permissionsApi";
import { RoleEditorDialog } from "@/components/staff/role-editor-dialog";

export interface RolesTabProps {
  orgId: string;
}

function memberCountLabel(count: number | undefined): string {
  const n = count ?? 0;
  if (n === 1) return "1 member";
  return `${n} members`;
}

function groupSummaryLabel(permissions: string[]): string {
  const groups = roleGroupSummary(permissions);
  if (groups.length === 0) return "No permissions";
  return groups.join(" + ");
}

export function RolesTab({ orgId }: RolesTabProps) {
  const [roles, setRoles] = useState<OrgRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor: editorRole === undefined means closed; null means "new role"; an
  // OrgRole means edit that role.
  const [editorRole, setEditorRole] = useState<OrgRole | null | undefined>(
    undefined,
  );

  const [pendingDelete, setPendingDelete] = useState<OrgRole | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listOrgRoles(orgId);
      setRoles(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load roles.");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listOrgRoles(orgId);
        if (!cancelled) setRoles(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load roles.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteRole(pendingDelete.role_id);
      await load();
      setPendingDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete role.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Typography color="text.secondary" variant="body2">
          Roles bundle the permissions you grant your team.
        </Typography>
        <Button
          disabled={loading}
          onClick={() => setEditorRole(null)}
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
        >
          New role
        </Button>
      </Stack>

      {/* The focused editor is modal: while it's open the list behind it is
          irrelevant, so we don't render it. This also keeps the editor's group
          headings (e.g. "Operations") the single source of that text — the
          list's group SUMMARY ("Operations + Programs") isn't competing in the
          DOM while editing. */}
      {editorRole !== undefined ? null : error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Box
          aria-label="Loading roles"
          role="status"
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Paper key={i} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Skeleton height={24} width={128} />
                <Skeleton height={20} width={160} />
                <Skeleton height={20} width={80} />
              </Stack>
            </Paper>
          ))}
        </Box>
      ) : roles.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ borderStyle: "dashed", p: 5, textAlign: "center" }}
        >
          <ShieldOutlinedIcon color="action" sx={{ fontSize: 36 }} />
          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
            No roles yet
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Create a role to grant staff specific permissions.
          </Typography>
          <Button
            onClick={() => setEditorRole(null)}
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            variant="outlined"
          >
            New role
          </Button>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          {roles.map((role) => (
            <Paper
              key={role.role_id}
              onClick={() => setEditorRole(role)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setEditorRole(role);
                }
              }}
              role="button"
              tabIndex={0}
              variant="outlined"
              sx={{
                cursor: "pointer",
                p: 2,
                transition: (theme) =>
                  theme.transitions.create([
                    "background-color",
                    "border-color",
                  ]),
                "&:hover": {
                  bgcolor: "action.hover",
                  borderColor: "primary.main",
                },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    variant="subtitle1"
                    sx={{ fontWeight: 700 }}
                  >
                    {role.name}
                  </Typography>
                  <Typography color="text.secondary" noWrap variant="body2">
                    {groupSummaryLabel(role.permissions)}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: "center" }}
                  >
                    <PeopleOutlineIcon color="action" fontSize="small" />
                    <Typography color="text.secondary" variant="caption">
                      {memberCountLabel(role.member_count)}
                    </Typography>
                  </Stack>
                </Stack>
                <IconButton
                  aria-label={`Delete role ${role.name}`}
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingDelete(role);
                  }}
                  size="small"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      {/* Focused single-role editor (edit OR create) */}
      <RoleEditorDialog
        orgId={orgId}
        role={editorRole ?? null}
        open={editorRole !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditorRole(undefined);
        }}
        onSaved={load}
      />

      {/* Delete confirm dialog */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        open={Boolean(pendingDelete)}
      >
        <DialogTitle>Delete role?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes <strong>{pendingDelete?.name}</strong>.
            Staff currently holding this role will lose its permissions.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleting}
            onClick={() => setPendingDelete(null)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleting}
            onClick={() => void confirmDelete()}
            size="small"
            variant="contained"
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
